import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';

// label is the task name; data holds avgCycleTime (hours) per month, aligned to monthLabels
export type TaskCycleTimeBarSeries = {
    label: string;
    data: number[];
};

const HEAT_LIGHT = { r: 227, g: 242, b: 253 };
const HEAT_DARK = { r: 13, g: 71, b: 161 };

type HeatCell = { color: string; textColor: string; full: string; tooltip: string };
type HeatRow = { label: string; cells: HeatCell[] };

@Component({
    selector: 'task-cycle-time-heat-map',
    templateUrl: './task-cycle-time-heat-map.component.html',
    styleUrl: './task-cycle-time-heat-map.component.scss',
    imports: [SharedModule]
})
export class TaskCycleTimeHeatMapComponent implements OnChanges {
    @Input() series: TaskCycleTimeBarSeries[] = [];
    @Input() monthLabels: string[] = [];
    @Input() isExpanded = false;

    rows: HeatRow[] = [];
    columnLabels: string[] = [];

    // When set, tasks are ordered by this column's value; otherwise original (alphabetical) order
    sortColumnIndex: number | null = null;
    sortDirection: 'asc' | 'desc' = 'desc';
    // When set, tasks are ordered alphabetically by name (takes precedence over month sort)
    nameSortDirection: 'asc' | 'desc' | null = null;

    get gridTemplateColumns(): string {
        return `minmax(0, 1.6fr) repeat(${this.columnLabels.length}, minmax(0, 1fr))`;
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['series'] || simpleChanges['monthLabels'] || simpleChanges['isExpanded']) {
            // Column set changes when toggling expand, so drop any active sort
            if (simpleChanges['isExpanded']) {
                this.sortColumnIndex = null;
            }
            this.build();
        }
    }

    // Up arrow (desc) = largest first, down arrow (asc) = lowest first; clicking active arrow clears the sort
    sortByColumn(columnIndex: number, direction: 'asc' | 'desc'): void {
        this.nameSortDirection = null;
        if (this.sortColumnIndex === columnIndex && this.sortDirection === direction) {
            this.sortColumnIndex = null;
        } else {
            this.sortColumnIndex = columnIndex;
            this.sortDirection = direction;
        }
        this.build();
    }

    // Clicking the month label cycles: desc -> asc -> back to alphabetical
    toggleSort(columnIndex: number): void {
        this.nameSortDirection = null;
        if (this.sortColumnIndex !== columnIndex) {
            this.sortColumnIndex = columnIndex;
            this.sortDirection = 'desc';
        } else if (this.sortDirection === 'desc') {
            this.sortDirection = 'asc';
        } else {
            this.sortColumnIndex = null;
        }
        this.build();
    }

    // A-Z (asc) or Z-A (desc) by task name; clicking the active arrow clears the sort
    sortByName(direction: 'asc' | 'desc'): void {
        this.sortColumnIndex = null;
        this.nameSortDirection = this.nameSortDirection === direction ? null : direction;
        this.build();
    }

    // Clicking the Task Name header cycles: A-Z -> Z-A -> original order
    toggleNameSort(): void {
        this.sortColumnIndex = null;
        if (this.nameSortDirection === null) {
            this.nameSortDirection = 'asc';
        } else if (this.nameSortDirection === 'asc') {
            this.nameSortDirection = 'desc';
        } else {
            this.nameSortDirection = null;
        }
        this.build();
    }

    hasActiveSort(): boolean {
        return this.sortColumnIndex !== null || this.nameSortDirection !== null;
    }

    resetSort(): void {
        this.sortColumnIndex = null;
        this.nameSortDirection = null;
        this.build();
    }

    private build(): void {
        const months = this.monthLabels;
        if (!months.length || !this.series.length) {
            this.rows = [];
            this.columnLabels = [];
            return;
        }

        // Collapsed: single 6-month rolling-average column per task. Expanded: full month grid.
        this.columnLabels = this.isExpanded ? [...months] : ['6-Month Average'];
        const taskValues = this.series.map(task => this.isExpanded
            ? months.map((_, i) => task.data[i] ?? 0)
            : [this.rollingAverage(task.data)]);

        let min = Infinity;
        let max = -Infinity;
        taskValues.forEach(values => values.forEach(v => {
            min = Math.min(min, v);
            max = Math.max(max, v);
        }));
        if (!Number.isFinite(min)) { min = 0; max = 0; }
        const span = max - min || 1;

        if (this.sortColumnIndex !== null && this.sortColumnIndex >= this.columnLabels.length) {
            this.sortColumnIndex = null;
        }

        let order = this.series.map((_, i) => i);
        if (this.nameSortDirection) {
            const dir = this.nameSortDirection;
            order = order.sort((a, b) => {
                const cmp = (this.series[a].label ?? '').localeCompare(this.series[b].label ?? '', undefined, { numeric: true });
                return dir === 'asc' ? cmp : -cmp;
            });
        } else if (this.sortColumnIndex !== null) {
            const col = this.sortColumnIndex;
            const dir = this.sortDirection;
            order = order.sort((a, b) => {
                const va = taskValues[a][col] ?? 0;
                const vb = taskValues[b][col] ?? 0;
                return dir === 'desc' ? vb - va : va - vb;
            });
        }

        this.rows = order.map(taskIndex => ({
            label: this.series[taskIndex].label,
            cells: taskValues[taskIndex].map(v => {
                const t = (v - min) / span;
                const full = this.formatFull(v / 24);
                return {
                    color: this.heatColor(t),
                    textColor: t > 0.55 ? '#FFFFFF' : '#0B0B0B',
                    full,
                    tooltip: `${this.series[taskIndex].label}: ${full}`
                };
            })
        }));
    }

    // Average cycle time across months where the task occurred (ignores empty months)
    private rollingAverage(data: number[]): number {
        const active = (data ?? []).filter(v => (v ?? 0) > 0);
        if (!active.length) return 0;
        return active.reduce((sum, v) => sum + v, 0) / active.length;
    }

    private heatColor(t: number): string {
        const clamped = Math.max(0, Math.min(1, t));
        const r = Math.round(HEAT_LIGHT.r + (HEAT_DARK.r - HEAT_LIGHT.r) * clamped);
        const g = Math.round(HEAT_LIGHT.g + (HEAT_DARK.g - HEAT_LIGHT.g) * clamped);
        const b = Math.round(HEAT_LIGHT.b + (HEAT_DARK.b - HEAT_LIGHT.b) * clamped);
        return `rgb(${r}, ${g}, ${b})`;
    }

    formatFull(value: number): string {
        if (value === 0) return '0';
        let label = '';
        const hours = Math.ceil(value * 24);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            label += days + (days === 1 ? ' day' : ' days');
        }
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
            if (label) label += ' ';
            label += remainingHours + (remainingHours === 1 ? ' hour' : ' hours');
        }
        return label;
    }
}

